import { useArcVoiceCall } from '../../contexts/ArcVoiceCallContext'
import {
  AgendaItemPhase,
  agendaItemPhase,
  ArcVoiceAgendaStatus,
  IArcVoiceAgendaItem,
} from '../../helpers/arcVoice/callAgenda'
import { isRunningOffStage } from '../../helpers/arcVoice/callPresentation'
import useCallClock from '../../hooks/useCallClock'
import ArcActivityCard, { ArcActivityTone } from '../ArcPresence/ArcActivityCard'
import { IconName } from '../Common/Icon'

/** A told or stopped item stays on screen this long, so how it ended is seen before it goes. */
const TOLD_LINGER_MS = 4000
const MAX_SHOWN = 4

interface IFloatState {
  icon: IconName
  label: string
  tone?: ArcActivityTone
}

const FLOAT_STATES: Record<AgendaItemPhase, IFloatState> = {
  [AgendaItemPhase.Working]: { icon: 'loader', label: 'Working', tone: ArcActivityTone.Working },
  [AgendaItemPhase.Parked]: { icon: 'pin', label: 'Parked for later' },
  [AgendaItemPhase.Stopped]: { icon: 'stop', label: 'Stopped', tone: ArcActivityTone.Stopped },
  [AgendaItemPhase.Failed]: { icon: 'alert-circle', label: 'Didn’t go through' },
  [AgendaItemPhase.NeedsApproval]: { icon: 'check-shield', label: 'Needs your OK' },
  [AgendaItemPhase.Ready]: {
    icon: 'check-circle',
    label: 'Back, telling you next',
    tone: ArcActivityTone.Done,
  },
  [AgendaItemPhase.Done]: { icon: 'check-circle', label: 'Told you', tone: ArcActivityTone.Done },
}

/** What is in flight, what is back and waiting to be said, and what was just said or stopped. */
const floatingItems = (agenda: IArcVoiceAgendaItem[], now: number): IArcVoiceAgendaItem[] =>
  agenda
    .filter(
      ({ status, updatedAt }) =>
        status === ArcVoiceAgendaStatus.Working ||
        status === ArcVoiceAgendaStatus.Ready ||
        ((status === ArcVoiceAgendaStatus.Told || status === ArcVoiceAgendaStatus.Stopped) &&
          now - updatedAt < TOLD_LINGER_MS),
    )
    .slice(-MAX_SHOWN)

/**
 * The call's agenda as it moves, in Arc's activity rail over whatever page they're
 * on: each request spins while Arc works on it and turns to a check when it is
 * back, so work in the background is visible without opening the call.
 */
const ArcVoiceWorkingItems = () => {
  const { snapshot, isStageVisible } = useArcVoiceCall()
  // Re-rendered by the call clock, so a told item lingers and then leaves.
  useCallClock(snapshot)
  if (!isRunningOffStage(snapshot, isStageVisible)) return null
  return (
    <>
      {floatingItems(snapshot.agenda, Date.now()).map((item) => {
        const state = FLOAT_STATES[agendaItemPhase(item)]
        return (
          <ArcActivityCard
            key={item.id}
            icon={state.icon}
            tone={state.tone}
            title={item.title}
            status={state.label}
          />
        )
      })}
    </>
  )
}

export default ArcVoiceWorkingItems
